// src/redux/api/walletApi.ts
import { baseApi } from "./baseApi";

//  Bank types
export interface Bank {
  name: string;
  code: string;
  nipBankCode?: string;
}

//  Verify Account types
export interface VerifyAccountRequest {
  accountNumber: string;
  bankCode: string;
}

export interface VerifyAccountResponse {
  status: string;
  data: {
    account_name: string;
  };
}

//  Verify BVN types
export interface VerifyBvnRequest {
  bvn: string;
}

export interface VerifyBvnResponse {
  status: string;
  message?: string;
  data?: {
    account_name?: string;
    [key: string]: any;
  };
}

//  Create Wallet types
export interface CreateWalletRequest {
  bvn: string;
  pin_hashed: string;
  preferred_account_number: string;
  preferred_bank_name: string;
  account_name: string;

}

export interface CreateWalletResponse {
  status: string;
  // Shape beyond `status` isn't pinned down here — includes bvn_details on
  // success, read defensively wherever it's consumed rather than asserted.
  data: any;
}

//  Wallet info types
export interface WalletResponse {
  status: boolean;
  data: {
    data: {
      wallet: {
        id: string;
        wallet_profile_id: string;
        user_id: string;
        company_id: string;
        balance: string;
        monnify_balance: string;
        pin_hashed: string;
        status: string;
        bvn: string | null;
        bvnDateOfBirth: string | null;
        created_at: string;
        updated_at: string;
        account_reference: string;
        account_number: string;
        account_name: string;
        bank_name: string;
        bank_code: string;
        bank_slug: string;
        balance_trend?: {
          trend_percentage: number;
          trend: "increase" | "decrease";
        };

        wallet_profile: {
          id: string;
          user_id: string;
          business_name: string;
          display_name: string;
          business_type_id: string | null;
          currency: string | null;
          created_at: string;
          updated_at: string;
        };
      };
    };
  };
}

//  Transaction types
export interface Transaction {
  id: string;
  date: string;
  type: string;
  amount: string;
  description: string;
  status: string;
  // Fee breakdown — total_fee is 0 (and provider_fee/platform_fee absent) for credits.
  provider_fee?: number;
  platform_fee?: number;
  total_fee?: number;
  total_debit?: number;
}

//  Updated to match  paginated response format
export interface GetTransactionsResponse {
  status: string;
  data: {
    current_page: number;
    data: Transaction[];
    first_page_url: string;
    from: number | null;
    last_page: number;
    last_page_url: string;
    links: {
      url: string | null;
      label: string;
      active: boolean;
    }[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number | null;
    total: number;
  };
}

//  Transfer types
export interface TransferRequest {
  amount: number;
  account_number: string;
  beneficiary_id?: number;
  bank_code: string;
  account_name: string;
  description: string;
  transaction_pin: string;
  // optional idempotency key to prevent duplicate transactions
  idempotency_key?: string;
}

export interface FeeBreakdown {
  amount: number;
  provider_fee: number;
  platform_fee: number;
  total_fee: number;
  total_debit: number;
}

export interface TransferResponse {
  status: string;
  data: {
    message: string;
    result: {
      status: string;
      message: string;
      transaction: Record<string, any>;
      fee_breakdown: FeeBreakdown;
    };
  };
}

//  Beneficiary types
export interface AddBeneficiaryRequest {
  account_name: string;
  account_number: string;
  bank_code: string;
  bank_name: string;
}



export interface AddBeneficiaryResponse {
  status: string;
  data: string;
}
//  Get Beneficiaries types
export interface Beneficiary {
  id: string;
  user_id: string;
  account_name: string;
  account_number: string;
  bank_code: string;
  bank_name: string;
  created_at: string;
  updated_at: string;
}

export interface GetBeneficiariesResponse {
  status: string;
  data: Beneficiary[];
}

// Add the interface
export interface WalletSummary {
  total_withdrawals: { amount: string; count: number };
  pending_withdrawals: { amount: string; count: number };
  successful_withdrawals: {
    amount: string;
    count: number;
    last_date: string | null;
    channel: string | null;
  };
  financial_trend: { month: string; total: number }[];
  wallet_activity: { month: string; credits: number; debits: number }[];
}

export interface WalletSummaryResponse {
  status: string;
  data: WalletSummary;
}

export const walletApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    //  Fetch bank list
    getBanks: builder.query<Bank[], void>({
      query: () => ({
        url: "/wallet/wallet/banks",
        method: "GET",
      }),
      providesTags: ["banks"],
      // Confirmed actual shape: { data: { banks: { banks: [...] } } } — the
      // "banks" key is doubly-nested. A truthy non-array anywhere along an
      // assumed path (e.g. `data.banks` resolving to that inner
      // `{ banks: [...] }` object) sails past `?? []` and crashes
      // `.filter()` downstream, so try every shape seen and only accept
      // the first one that's a genuine array.
      transformResponse: (response: any): Bank[] => {
        const candidates = [
          response,
          response?.data,
          response?.banks,
          response?.data?.banks,
          response?.data?.banks?.banks,
          response?.data?.data,
          response?.data?.banks?.data,
        ];
        return candidates.find((candidate) => Array.isArray(candidate)) ?? [];
      },
    }),

    //  Verify bank account
    verifyAccount: builder.mutation<
      VerifyAccountResponse,
      VerifyAccountRequest
    >({
      query: (body) => ({
        url: "/wallet/verify-account",
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => response,
    }),

    //  Verify BVN — step 1 of wallet setup, triggered on demand (as the
    // user finishes typing) rather than automatically, hence a lazy query.
    verifyBvn: builder.query<VerifyBvnResponse, VerifyBvnRequest>({
      query: ({ bvn }) => ({
        url: `/wallet/verify-bvn?bvn=${encodeURIComponent(bvn)}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response,
    }),

    //  Create wallet
    createWallet: builder.mutation<CreateWalletResponse, CreateWalletRequest>(
      {
        query: (body) => ({
          url: "/wallet/createWallet",
          method: "POST",
          body,
        }),
        transformResponse: (response: any) => response,
      }
    ),

    //  Get wallet info
    getWallet: builder.query<WalletResponse, string>({
      query: (companyId) => ({
        url: `/wallet/get-wallet?company_id=${companyId}`,
        method: "GET",
      }),
      providesTags: ["wallet"],
      transformResponse: (response: any) => response,
    }),


    //  Get wallet transactions
    getTransactions: builder.query<
      GetTransactionsResponse,
      {
        page?: number;
        per_page?: number;
        search?: string;
        status?: "pending" | "failed" | "completed";
        sort_by_amount?: "highest" | "lowest";
      } | void
    >({
      query: (args) => {
        const { page, per_page, search, status, sort_by_amount } = args || {};
        const params = new URLSearchParams();
        if (page) params.append("page", String(page));
        if (per_page) params.append("per_page", String(per_page));
        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (sort_by_amount) params.append("sort_by_amount", sort_by_amount);
        const query = params.toString();
        return {
          url: `/wallet/transactions${query ? `?${query}` : ""}`,
          method: "GET",
        };
      },
      providesTags: ["transactions"],
      transformResponse: (response: any) => response,
    }),

    //  Transfer money
    transfer: builder.mutation<TransferResponse, TransferRequest>({
      query: (body) => ({
        url: "/wallet/wallet/transfer",
        method: "POST",
        headers: {
          // caller supplies a stable key per withdrawal attempt so retries/double-submits dedupe on the backend
          "Idempotency-key": body.idempotency_key || crypto.randomUUID(),
        },
        body,
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: ["transactions", "wallet"],
    }),

    //  Preview the fee breakdown for a withdrawal amount, before transferring
    calculateTransferFee: builder.mutation<
      { status: string; data: FeeBreakdown },
      { amount: number }
    >({
      query: ({ amount }) => ({
        url: `/wallet/wallet/transfer/fee?amount=${amount}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response,
    }),

    addBeneficiary: builder.mutation<
      AddBeneficiaryResponse,
      AddBeneficiaryRequest
    >({
      query: (body) => ({
        url: "/beneficiaries",
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: ["beneficiaries"], // optional cache invalidation
    }),
    getBeneficiaries: builder.query<GetBeneficiariesResponse, void>({
      query: () => ({
        url: "/beneficiaries",
        method: "GET",
      }),
      providesTags: ["beneficiaries"],
      transformResponse: (response: any) => response,
    }),
    deleteBeneficiary: builder.mutation<
      { status: string; data: string },
      string
    >({
      query: (id) => ({
        url: `/beneficiaries/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: ["beneficiaries"], // Refresh cached list after deletion
    }),
    changePin: builder.mutation<
      {
        status: string;
        data?: { message?: string };
        error?: { message?: string };
      },
      { old_pin: string; new_pin: string }
    >({
      query: (body) => ({
        url: "/wallet/wallet/change-pin",
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => response,
    }),
    updateWalletPreference: builder.mutation<
      {
        status: string;
        data: {
          user_id: string;
          id: string;
          updated_at: string;
          created_at: string;
          notify_on_success: boolean;
          notify_on_failure: boolean;
          notify_on_transaction: boolean;
        };
      },
      {
        notify_on_success: boolean;
        notify_on_failure: boolean;
        notify_on_transaction: boolean;
      }
    >({
      query: (body) => ({
        url: "/wallet-preference/update-preference",
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => response,
    }),

    // Get wallet summary and graph data
    getWalletSummary: builder.query<WalletSummaryResponse, void>({
      query: () => ({
        url: "/wallet/summary",
        method: "GET",
      }),
      providesTags: ["wallet"],
      transformResponse: (response: any) => response,
    }),
    getWalletPreference: builder.query<
      {
        status: string;
        data: {
          id: string;
          user_id: string;
          notify_on_success: number;
          notify_on_failure: number;
          notify_on_transaction: number;
          created_at: string;
          updated_at: string;
        };
      },
      void
    >({
      query: () => ({
        url: "/wallet-preference/get-preference",
        method: "GET",
      }),
      transformResponse: (response: any) => response,
      providesTags: ["wallet-preference"],
    }),
  }),
});

export const {
  useGetBanksQuery,
  useVerifyAccountMutation,
  useLazyVerifyBvnQuery,
  useCreateWalletMutation,
  useGetWalletQuery,
  useGetTransactionsQuery,
  useTransferMutation,
  useCalculateTransferFeeMutation,
  useAddBeneficiaryMutation,
  useGetBeneficiariesQuery,
  useDeleteBeneficiaryMutation,
  useChangePinMutation,
  useUpdateWalletPreferenceMutation,
  useGetWalletPreferenceQuery,
  useGetWalletSummaryQuery,
} = walletApi;
